import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { X, ArrowRight, CheckCircle2, AlertCircle, Loader2, Send } from "lucide-react";
import { api, apiError, eur } from "@/lib/apiClient";
import { PRODUCT_CATALOG } from "@/lib/productCatalog";

function Item({ row, code, clienteId, onRemove, onGo }) {
  const p = PRODUCT_CATALOG[code];
  if (!p) return null;
  const q = row?.quote;
  const go = () => (p.external ? window.open(p.external, "_blank", "noopener") : onGo(`${p.to}?anagrafica=${clienteId}`));
  return (
    <div className="rounded-xl border p-3" data-testid={`trattativa-item-${code}`}>
      <div className="flex items-center gap-3">
        <div className="grid h-10 w-10 place-items-center rounded-lg bg-sage"><p.icon className="h-5 w-5 text-forest" /></div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-forest">{p.label}{q?.premio_finale ? <span className="font-normal text-muted-foreground"> · {eur(q.premio_finale)}</span> : null}</div>
          <div data-testid={`trattativa-item-${code}-status`} className={`flex items-center gap-1 text-xs ${row?.ready ? "text-terracotta" : "text-amber-700"}`}>
            {row?.ready ? <><CheckCircle2 className="h-3.5 w-3.5" /> Pronto all'emissione{q?.codice_prev ? ` · ${q.codice_prev}` : ""}</> : <><AlertCircle className="h-3.5 w-3.5 shrink-0" /> {row?.reason || "…"}</>}
          </div>
        </div>
        <button onClick={() => onRemove(code)} className="text-xs text-muted-foreground hover:text-red-600" data-testid={`trattativa-remove-${code}`}>Rimuovi</button>
      </div>
      <div className="mt-2 flex gap-2">
        <button onClick={go} data-testid={`trattativa-calc-${code}`} className="flex-1 rounded-lg border border-forest py-1.5 text-xs font-semibold text-forest hover:bg-sage">{q ? "Nuovo calcolo" : "Calcola premio"}</button>
        {q && <button onClick={() => onGo(`/preventivi/${q.id}`)} data-testid={`trattativa-open-${code}`} className="flex-1 rounded-lg bg-sage py-1.5 text-xs font-semibold text-forest">Apri pratica</button>}
      </div>
    </div>
  );
}

export function TrattativaDrawer({ open, onClose, current, items, others, onSwitch, onRemove, onAnnulla, onSent, onChangeClient }) {
  const navigate = useNavigate();
  const [st, setSt] = useState(null);
  const [sending, setSending] = useState(false);
  const tid = current?.id;
  const key = items.join(",");
  useEffect(() => {
    setSt(null);
    if (open && tid) api.get(`/trattative/${tid}/stato`).then(({ data }) => setSt(data)).catch(() => {});
  }, [open, tid, key]);
  if (!open) return null;
  const go = (to) => { onClose(); navigate(to); };
  const row = (c) => st?.items.find((r) => r.code === c);
  const emetti = async () => {
    setSending(true);
    try {
      const { data } = await api.post(`/trattative/${tid}/emetti`);
      toast.success(`Proposta unica inviata a ${data.destinatario}`);
      onSent(); onClose();
    } catch (e) { toast.error(apiError(e)); } finally { setSending(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-forest/30" onClick={onClose}>
      <div className="flex h-full w-full max-w-md flex-col bg-white shadow-2xl animate-in slide-in-from-right" onClick={(e) => e.stopPropagation()} data-testid="trattativa-drawer">
        <div className="flex items-start justify-between border-b p-5">
          <div>
            <div className="font-heading text-xl font-semibold text-forest">Trattativa <span className="ml-1 rounded-full bg-forest px-2 py-0.5 text-xs text-white">{items.length}</span></div>
            {current ? <div className="text-sm text-muted-foreground" data-testid="trattativa-cliente">Cliente: <b className="text-forest">{current.cliente_nome}</b> · <button onClick={onChangeClient} className="text-terracotta hover:underline">altro cliente</button></div>
              : <button onClick={onChangeClient} className="text-sm font-semibold text-terracotta" data-testid="trattativa-choose-client">Scegli il cliente</button>}
          </div>
          <button onClick={onClose} data-testid="trattativa-close"><X className="h-5 w-5 text-muted-foreground" /></button>
        </div>
        <div className="flex-1 space-y-3 overflow-y-auto p-5">
          {current && items.length === 0 && <p className="text-sm text-muted-foreground">Nessun prodotto ancora. Dal catalogo premi «Aggiungi alla trattativa».</p>}
          {items.map((c) => <Item key={c} code={c} row={row(c)} clienteId={current.anagrafica_id} onRemove={onRemove} onGo={go} />)}
          {others.length > 0 && (
            <div className="pt-3" data-testid="trattativa-others">
              <div className="mb-2 text-[11px] font-bold uppercase tracking-wide text-muted-foreground">Altre trattative aperte</div>
              {others.map((t) => (
                <button key={t.id} onClick={() => onSwitch(t.id)} data-testid={`trattativa-switch-${t.id}`} className="flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm hover:bg-sage">
                  <span className="font-medium text-forest">{t.cliente_nome}</span><span className="text-xs text-muted-foreground">{(t.items || []).length} prodotti</span>
                </button>
              ))}
            </div>
          )}
        </div>
        {current && (
          <div className="space-y-3 border-t p-5">
            <div className="flex justify-between text-sm"><span className="text-muted-foreground">Totale premi</span><span className="font-semibold text-forest" data-testid="trattativa-totale">{st?.totale ? eur(st.totale) : "dopo il calcolo"}</span></div>
            {st?.blocchi?.map((b) => <div key={b} className="text-xs text-amber-700">{b}</div>)}
            <button onClick={emetti} disabled={!st?.pronta || sending} data-testid="trattativa-emetti"
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-forest py-3 text-xs font-bold uppercase text-white transition-colors hover:bg-forest-hover disabled:cursor-not-allowed disabled:bg-slate-300">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Invia proposta unica al cliente
            </button>
            {st && !st.pronta && items.length > 0 && <p className="text-center text-[11px] text-muted-foreground">Si attiva quando tutti i prodotti sono pronti all'emissione.</p>}
            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => go("/")} className="flex items-center justify-center gap-1 rounded-lg border border-forest py-2.5 text-xs font-bold uppercase text-forest" data-testid="trattativa-add-more">Aggiungi altro <ArrowRight className="h-3.5 w-3.5" /></button>
              <button onClick={onAnnulla} className="rounded-lg border py-2.5 text-xs font-bold uppercase text-muted-foreground" data-testid="trattativa-reset">Annulla trattativa</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
